/**
 * useJointOwnerAdd — adds a Joint Owner relationship to an existing account.
 *
 * The actual person-capture flow (CIP scan, identity, address, employment)
 * is handed off to the unified PersonDialog (individual category). This
 * hook picks up AFTER the new CIF has been created and writes the
 * CustRelAdd record tying the joint CIF to the target account.
 *
 * If the dialog is run in "existing CIF" mode (operator picked a pre-
 * existing person), we skip straight to CustRelAdd — no CustAdd needed.
 *
 * Ops:
 *   1. CustRelAdd with RelCode='JO' (Joint Owner) binding joint CIF to acct.
 *   2. CustMsgAdd audit note on the primary CIF.
 *   3. Session events: joint.owner.added, acct.rel.added.
 *   4. Auto-task: "Signature card needs all signers" (priority 3).
 */

import { useCallback } from 'react';
import type { CustRelAddRequest } from '@retail-ops/shared-types';
import type { AcctType } from '@retail-ops/shared-types/enums';
import { useApi } from '../../hooks/useApi';
import { useEmitEvent } from '../../hooks/useEmitEvent';
import type { MaintActionResult } from '../types';

export interface JointOwnerAddInput {
  primaryCifId: string;
  jointCifId: string;
  acctId: string;
  acctType: AcctType | string;
  officerCode: string;
  /** If the joint CIF was just created by PersonDialog, pass true to adjust
   *  the audit-note wording. */
  newCif?: boolean;
  jointDisplayName?: string;
}

export interface UseJointOwnerAddApi {
  run: (input: JointOwnerAddInput) => Promise<MaintActionResult>;
}

export function useJointOwnerAdd(): UseJointOwnerAddApi {
  const { client } = useApi();
  const emit = useEmitEvent();

  const run = useCallback(
    async (input: JointOwnerAddInput): Promise<MaintActionResult> => {
      try {
        const req: CustRelAddRequest = {
          primaryCifId: input.primaryCifId,
          jointCifId: input.jointCifId,
          acctId: input.acctId,
          acctType: input.acctType as AcctType,
          custRelCode: 'Joint',
          acctRelCode: 'AO',
          fdicCat: 'JO',
          offCode: input.officerCode,
          operatorNumber: input.officerCode,
        };
        await client.accounts.custRelAdd(req);

        const today = new Date().toISOString().slice(0, 10);
        const joinedName = input.jointDisplayName ?? `CIF ${input.jointCifId}`;
        const auditNote =
          `Joint Owner added on ${today} by ${input.officerCode}: ${joinedName}` +
          ` linked to acct ${input.acctId}` +
          (input.newCif ? ' (new CIF)' : ' (existing CIF)');

        try {
          await client.jxrl.post('Customer/CustMsgAdd', {
            CustId: input.primaryCifId,
            MsgType: 'AN',
            Msg: auditNote,
            OperatorNumber: input.officerCode,
          });
        } catch {
          /* non-fatal */
        }

        emit({
          eventType: 'joint.owner.added',
          eventCategory: 'cif-account',
          payload: {
            primaryCifId: input.primaryCifId,
            jointCifId: input.jointCifId,
            acctId: input.acctId,
            acctType: input.acctType,
            newCif: Boolean(input.newCif),
          },
        });
        emit({
          eventType: 'acct.rel.added',
          eventCategory: 'cif-account',
          payload: {
            acctId: input.acctId,
            acctType: input.acctType,
            relCode: 'JO',
          },
        });

        // Auto-create signature-card task — procedure: every ownership change
        // requires reprint + all-signer signature.
        try {
          await client.retail.tasks.create({
            title: 'Signature card — add joint owner signature',
            type: 'signature-card-update',
            category: 'workflow',
            priority: 'Normal',
            assignedTo: input.officerCode,
            cifId: input.primaryCifId,
            severity: 'info',
            autoCreated: true,
            description:
              `Joint Owner ${joinedName} added to acct ${input.acctId}. ` +
              `Reprint signature card and collect all signers.`,
          });
        } catch {
          /* non-fatal */
        }

        // TODO: wire disclosure packet when Team 2's engine lands
        //       (updated signature card + CIP acknowledgment for joint).

        return { ok: true, message: `Joint Owner linked to ${input.acctId}.` };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
      }
    },
    [client, emit],
  );

  return { run };
}
